import React from 'react';
import { SegmentedControl } from '@parsina/framework';

import { useTranslation } from '../../../i18n/I18nProvider.jsx';
import SoapEditor from '../../../components/clinical/SoapEditor.jsx';
import DiagnosisPanel from '../../../components/clinical/DiagnosisPanel.jsx';
import RxPanel from '../../../components/clinical/RxPanel.jsx';
import OrdersPanel from '../../../components/clinical/OrdersPanel.jsx';
import { WORK_TABS } from '../Visit.shared.js';

// کار — the column the student writes in. Four tabs, in the doctor app's order,
// reachable with `Ctrl+1..4` (registered by the screen, not here — a panel that
// binds keys binds them again every time it re-mounts).
//
// It is deliberately presentational: every piece of state it renders belongs to
// `useEncounter` or `useCopilot` above it, because the closure dialog has to be
// able to ask «are any review sheets still open?» and a warning cannot be
// computed from state hidden inside a tab that is not currently mounted.

export function WorkTabs({ value, onChange }) {
  const { t } = useTranslation();
  return (
    <SegmentedControl
      options={WORK_TABS.map((id) => ({ value: id, label: t(`visit.tabs.${id}`) }))}
      value={value}
      onChange={onChange}
      ariaLabel={t('visit.tabs.aria')}
      data-testid="work-tabs"
    />
  );
}

export default function WorkPanel({ vm, tab }) {
  const { encounter, copilot, readOnly } = vm;

  if (tab === 'note') {
    return (
      <SoapEditor
        soap={vm.soap}
        onChange={vm.setSoapField}
        readOnly={readOnly}
        onSuggest={vm.requestSoapSuggestion}
        suggestCost={copilot.costs.soap}
        suggesting={copilot.busy === 'soap'}
      />
    );
  }

  if (tab === 'diagnoses') {
    return (
      <DiagnosisPanel
        diagnoses={encounter?.diagnoses || []}
        readOnly={readOnly}
        onAdd={vm.addDiagnosis}
        onUpdate={vm.updateDiagnosis}
        onDelete={vm.deleteDiagnosis}
        onReorder={vm.reorderDiagnoses}
        onDifferential={vm.requestDifferential}
        differential={copilot.differential}
        differentialCost={copilot.costs.differential}
        differentialBusy={copilot.busy === 'differential'}
        onOpenDifferential={() => vm.openSheet('differential')}
      />
    );
  }

  if (tab === 'rx') {
    return (
      <RxPanel
        items={encounter?.rxItems || []}
        readOnly={readOnly}
        onAdd={vm.addRxItem}
        onDelete={vm.deleteRxItem}
        onCheckInteractions={vm.requestInteractions}
        interactionCost={copilot.costs.interactions}
        interactionBusy={copilot.busy === 'interactions'}
        hasInteractionResult={!!copilot.interactions}
        onOpenInteractions={() => vm.openSheet('interactions')}
        onPrint={() => vm.openSheet('print')}
      />
    );
  }

  return (
    <OrdersPanel
      orders={encounter?.orders || []}
      readOnly={readOnly}
      onAdd={vm.addOrder}
      onCancel={vm.cancelOrder}
      onRecordResult={vm.recordOrderResult}
      onSuggest={vm.requestOrderSuggestions}
      suggestCost={copilot.costs.orders}
      suggestBusy={copilot.busy === 'orders'}
      hasSuggestions={!!copilot.orderSuggestion}
      onOpenSuggestions={() => vm.openSheet('orderSuggest')}
    />
  );
}
